import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { Button } from './Button'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  /** Right-aligned drawer (default) or a centred destructive confirmation. */
  variant?: 'drawer' | 'destructive'
  primaryLabel?: string
  onPrimary?: () => void
  primaryDisabled?: boolean
  primaryLoading?: boolean
  cancelLabel?: string
  width?: number
}

export function Modal({
  open, onClose, title, children, variant = 'drawer',
  primaryLabel, onPrimary, primaryDisabled, primaryLoading, cancelLabel = 'Cancel', width,
}: ModalProps) {
  const ref = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  /**
   * Callers pass `onClose` as an inline arrow, so its identity changes on every
   * parent render. Reading it through a ref keeps the focus effect below tied to
   * `open` alone: with `onClose` in the dependency list the effect tore down and
   * re-ran on every keystroke, stealing focus back to the close button.
   */
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
      if (e.key === 'Tab' && ref.current) {
        const focusable = ref.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        if (focusable.length === 0) return
        const first = focusable[0]!
        const last = focusable[focusable.length - 1]!
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }
    document.addEventListener('keydown', onKey)

    // Open on the first field, not the header close button, which is what a
    // document-order query returns.
    const previous = document.activeElement as HTMLElement | null
    const target =
      bodyRef.current?.querySelector<HTMLElement>(
        'input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"]), button',
      ) ?? ref.current?.querySelector<HTMLElement>('button')
    target?.focus()

    return () => { document.removeEventListener('keydown', onKey); previous?.focus() }
  }, [open])

  if (!open) return null

  const isDrawer = variant === 'drawer'
  const panelStyle: React.CSSProperties = isDrawer
    ? { width: width ?? 560, height: '100%', marginLeft: 'auto' }
    : { width: width ?? 420, margin: 'auto' }

  return (
    <div
      className="fixed inset-0 flex"
      style={{ zIndex: 60, backgroundColor: 'rgba(0,0,0,0.55)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex flex-col bg-background-body overflow-hidden"
        style={{ ...panelStyle, border: '0.5px solid #4B5563' }}
      >
        <div
          className="flex items-start justify-between flex-shrink-0"
          style={{ padding: 20, gap: 20, borderBottom: '0.5px solid #4B5563' }}
        >
          <h2 className="font-jakarta font-bold text-white" style={{ fontSize: 14, lineHeight: '22px' }}>{title}</h2>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}>
            <X size={20} className="text-text-support" />
          </button>
        </div>
        <div ref={bodyRef} className="flex-1 overflow-y-auto" style={{ padding: 20 }}>{children}</div>
        {(onPrimary || primaryLabel) && (
          <div
            className="flex items-center justify-end flex-shrink-0"
            style={{ padding: 20, gap: 10, borderTop: '0.5px solid #4B5563' }}
          >
            <Button variant="outline" size="medium" onClick={onClose}>{cancelLabel}</Button>
            <Button
              variant="filled-grey"
              size="medium"
              destructive={variant === 'destructive'}
              disabled={primaryDisabled}
              loading={primaryLoading}
              onClick={onPrimary}
            >
              {primaryLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
